import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../constants/api';
import ImageCropModal from './ImageCropModal';
import { useNotification } from '../../contexts/NotificationContext';
import { usePlan } from '../../contexts/PlanContext';
import { useLanguage } from '../../contexts/LanguageContext';

// ── Apple design tokens ──────────────────────────────────────────────────────
const T = {
  blue:   '#0071e3',
  green:  '#34c759',
  red:    '#ff3b30',
  dark:   '#1d1d1f',
  gray:   '#6e6e73',
  light:  '#aeaeb2',
  border: 'rgba(0,0,0,0.08)',
  bg:     'rgba(0,0,0,0.025)',
  font:   "-apple-system, BlinkMacSystemFont, 'SF Pro Text', sans-serif",
};

const PE_FONT = "'abcNormal','Inter','Arial',sans-serif";

const CSS = `
  @font-face {
    font-family: 'abcNormal';
    src: url('/fonts/ABCNormal_Normal.woff2') format('woff2');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }
  .pe-section{background:#fff;border-radius:16px;border:1px solid ${T.border};padding:24px;margin-bottom:16px;}
  .pe-label{display:block;font-size:12px;font-weight:500;color:${T.gray};margin-bottom:6px;letter-spacing:-.01em;font-family:${T.font};}
  .pe-input{width:100%;border:1px solid rgba(0,0,0,0.12);border-radius:9px;padding:9px 12px;font-size:14px;color:${T.dark};font-family:${T.font};letter-spacing:-.01em;background:#fff;outline:none;transition:border-color .15s,box-shadow .15s;box-sizing:border-box;}
  .pe-input:focus{border-color:${T.blue};box-shadow:0 0 0 3px rgba(0,113,227,0.12);}
  .pe-input::placeholder{color:${T.light};}
  .pe-hint{font-size:11px;color:${T.light};margin-top:4px;letter-spacing:-.01em;font-family:${T.font};}
  .pe-upload-btn{display:flex;align-items:center;justify-content:center;gap:7px;width:100%;padding:9px 16px;background:${T.dark};color:#fff;border:none;border-radius:9px;font-size:13px;font-weight:500;cursor:pointer;font-family:${T.font};letter-spacing:-.01em;transition:opacity .15s;}
  .pe-upload-btn:hover{opacity:.85;}
  .pe-upload-btn:disabled{opacity:.4;cursor:not-allowed;}
  .pe-del-btn{display:flex;align-items:center;justify-content:center;gap:6px;width:100%;padding:9px 16px;background:rgba(255,59,48,0.1);color:${T.red};border:none;border-radius:9px;font-size:13px;font-weight:500;cursor:pointer;font-family:${T.font};letter-spacing:-.01em;transition:background .15s;}
  .pe-del-btn:hover{background:rgba(255,59,48,0.16);}
  .pe-del-btn:disabled{opacity:.4;cursor:not-allowed;}
  .pe-img-drop{width:100%;height:148px;border-radius:12px;border:1.5px dashed rgba(0,0,0,0.15);background:rgba(0,0,0,0.02);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:8px;margin-bottom:8px;}
  .pe-link-row{background:${T.bg};border:1px solid rgba(0,0,0,0.06);border-radius:12px;padding:16px;margin-bottom:10px;}
  .pe-link-num{display:inline-flex;align-items:center;justify-content:center;width:20px;height:20px;border-radius:50%;background:rgba(0,113,227,0.1);color:${T.blue};font-size:11px;font-weight:600;flex-shrink:0;}
  .pe-sec-title{font-size:15px;font-weight:600;color:${T.dark};letter-spacing:-.02em;margin:0 0 16px;font-family:${T.font};}
  .pe-save-btn{display:inline-flex;align-items:center;gap:8px;padding:10px 28px;background:${T.dark};color:#fff;border:none;border-radius:10px;font-size:14px;font-weight:500;cursor:pointer;font-family:${T.font};letter-spacing:-.01em;transition:opacity .15s;}
  .pe-save-btn:hover{opacity:.85;}
  .pe-save-btn:disabled{opacity:.4;cursor:not-allowed;}

  /* Transfer-details toggle — same switch language as the rest of the
     dashboard's on/off controls (e.g. admin's "Cuenta activa" switch). */
  .pe-toggle{position:relative;display:inline-flex;height:24px;width:44px;align-items:center;border-radius:999px;border:none;cursor:pointer;transition:background .15s;flex-shrink:0;}
  .pe-toggle[data-on="true"]{background:${T.blue};}
  .pe-toggle[data-on="false"]{background:#d1d5db;}
  .pe-toggle-knob{display:block;height:18px;width:18px;border-radius:50%;background:#fff;transition:transform .15s;transform:translateX(3px);}
  .pe-toggle[data-on="true"] .pe-toggle-knob{transform:translateX(23px);}

  /* Free-plan lock — dims the control and shows a platform-styled tooltip
     on hover instead of hiding the feature outright. */
  .pe-locked{opacity:.42;filter:grayscale(.15);}
  .pe-locked, .pe-locked *{cursor:not-allowed!important;}
  [data-locked-tip]{position:relative;}
  [data-locked-tip]::after{
    content:attr(data-locked-tip);
    position:absolute;bottom:calc(100% + 9px);left:50%;
    transform:translateX(-50%) translateY(4px);
    background:#1d1d1f;color:#fff;font-size:12px;font-weight:500;letter-spacing:-0.01em;
    padding:6px 11px;border-radius:7px;white-space:nowrap;pointer-events:none;
    opacity:0;visibility:hidden;transition:opacity .12s ease,transform .12s ease;transition-delay:.05s;
    z-index:40;box-shadow:0 4px 14px rgba(0,0,0,0.2);font-family:${T.font};
  }
  [data-locked-tip]:hover::after{opacity:1;visibility:visible;transform:translateX(-50%) translateY(0);}
`;

// ── Mobile-only overrides (no tocan el layout de escritorio) ────────────────
const PE_MOBILE_CSS = `
@media (max-width: 768px) {
  .pe-mobile-empty { padding: 32px 0 !important; }
  .pe-section { padding: 17px !important; margin-bottom: 12px !important; }
  .pe-mobile-header { margin-bottom: 17px !important; }
  .pe-avatar-preview { height: 96px !important; }
  .pe-img-drop { height: 96px !important; }
  .pe-img-drop svg { width: 22px !important; height: 22px !important; }
  .pe-mobile-grid { gap: 10px !important; }
  .pe-mobile-save { padding-bottom: 17px !important; }
  .pe-upload-btn, .pe-del-btn { padding: 8px 10px !important; font-size: 11.5px !important; }
  .pe-sec-title { font-size: 13.5px !important; margin-bottom: 12px !important; }
}
`;

// Reusable input
const Field = ({ label, hint, children }) => (
  <div>
    {label && <label className="pe-label">{label}</label>}
    {children}
    {hint && <p className="pe-hint">{hint}</p>}
  </div>
);

const ProfileEditor = ({ profile, updateProfile, refreshProfile }) => {
  const { confirm, toast } = useNotification();
  const { hasFeature } = usePlan();
  const { t } = useLanguage();
  const canUseBackgroundImage = hasFeature('background_image');
  const [formData, setFormData] = useState({
    name: profile?.name || '',
    lastname: profile?.lastname || '',
    email: profile?.email || '',
    position: profile?.position || '',
    company: profile?.company || '',
    bio: profile?.bio || '',
    phone: profile?.phone || '',
    whatsapp: profile?.whatsapp || '',
    instagram: profile?.instagram || '',
    linkedin: profile?.linkedin || '',
    tiktok: profile?.tiktok || '',
    website: profile?.website || '',
    facebook: profile?.facebook || '',
    custom_link_1: profile?.custom_link_1 || profile?.custom_link || '',
    custom_link_title_1: profile?.custom_link_title_1 || profile?.custom_link_title || '',
    custom_link_2: profile?.custom_link_2 || '',
    custom_link_title_2: profile?.custom_link_title_2 || '',
    custom_link_3: profile?.custom_link_3 || '',
    custom_link_title_3: profile?.custom_link_title_3 || '',
    custom_link_4: profile?.custom_link_4 || '',
    custom_link_title_4: profile?.custom_link_title_4 || '',
    custom_file_name: profile?.custom_file_name || '',
    custom_file_name_2: profile?.custom_file_name_2 || '',
    custom_file_name_3: profile?.custom_file_name_3 || '',
    transfer_enabled: profile?.transfer_enabled || false,
    transfer_bank: profile?.transfer_bank || '',
    transfer_account_type: profile?.transfer_account_type || '',
    transfer_account_number: profile?.transfer_account_number || '',
    transfer_holder_name: profile?.transfer_holder_name || '',
    transfer_rut: profile?.transfer_rut || '',
    transfer_email: profile?.transfer_email || '',
  });
  const [saving, setSaving]               = useState(false);
  const [uploadingImage, setUploadingImage] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [imageToCrop, setImageToCrop]     = useState(null);

  useEffect(() => {
    if (profile) {
      setFormData({
        name: profile.name || '',
        lastname: profile.lastname || '',
        email: profile.email || '',
        position: profile.position || '',
        company: profile.company || '',
        bio: profile.bio || '',
        phone: profile.phone || '',
        whatsapp: profile.whatsapp || '',
        instagram: profile.instagram || '',
        linkedin: profile.linkedin || '',
        tiktok: profile.tiktok || '',
        website: profile.website || '',
        facebook: profile.facebook || '',
        custom_link_1: profile.custom_link_1 || profile.custom_link || '',
        custom_link_title_1: profile.custom_link_title_1 || profile.custom_link_title || '',
        custom_link_2: profile.custom_link_2 || '',
        custom_link_title_2: profile.custom_link_title_2 || '',
        custom_link_3: profile.custom_link_3 || '',
        custom_link_title_3: profile.custom_link_title_3 || '',
        custom_link_4: profile.custom_link_4 || '',
        custom_link_title_4: profile.custom_link_title_4 || '',
        custom_file_name: profile.custom_file_name || '',
        custom_file_name_2: profile.custom_file_name_2 || '',
        custom_file_name_3: profile.custom_file_name_3 || '',
        transfer_enabled: profile.transfer_enabled || false,
        transfer_bank: profile.transfer_bank || '',
        transfer_account_type: profile.transfer_account_type || '',
        transfer_account_number: profile.transfer_account_number || '',
        transfer_holder_name: profile.transfer_holder_name || '',
        transfer_rut: profile.transfer_rut || '',
        transfer_email: profile.transfer_email || '',
      });
    }
  }, [profile]);

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await updateProfile(formData);
      if (result.success) toast.success(t('profileEditor.toast.saved', 'Perfil actualizado correctamente'));
      else toast.error(result.error || t('profileEditor.toast.unknownError', 'Error desconocido'));
    } catch {
      toast.error(t('profileEditor.toast.saveError', 'No se pudo guardar el perfil. Intenta de nuevo.'));
    }
    setSaving(false);
  };

  const imageTypeLabel = (type, t) => {
    if (type === 'profile-image') return t('profileEditor.imageType.profile', 'imagen de perfil');
    if (type === 'company-logo') return t('profileEditor.imageType.logo', 'logo de empresa');
    return t('profileEditor.imageType.background', 'imagen de fondo');
  };
  const imageTypeLabelCap = (type, t) => {
    if (type === 'profile-image') return t('profileEditor.imageType.profileCap', 'Imagen de perfil');
    if (type === 'company-logo') return t('profileEditor.imageType.logoCap', 'Logo de empresa');
    return t('profileEditor.imageType.backgroundCap', 'Imagen de fondo');
  };

  const handleImageDelete = async (type) => {
    if (!profile?.id) { toast.error(t('profileEditor.toast.noProfile', 'No hay perfil seleccionado')); return; }
    const label = imageTypeLabel(type, t);
    if (!(await confirm(t('profileEditor.confirm.deleteImage', `¿Estás seguro de que deseas eliminar la ${label}?`).replace('{label}', label), { confirmLabel: t('profileEditor.delete', 'Eliminar') }))) return;
    setUploadingImage(type);
    try {
      await axios.delete(`${API}/upload/${type}/${profile.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const labelCap = imageTypeLabelCap(type, t);
      toast.success(t('profileEditor.toast.imageDeleted', `${labelCap} eliminada correctamente`).replace('{label}', labelCap));
      const fieldMap = { 'profile-image': 'profile_image', 'company-logo': 'company_logo', 'background-image': 'background_image' };
      const updatedProfile = { ...profile };
      delete updatedProfile[fieldMap[type]];
      if (updateProfile) updateProfile(updatedProfile);
    } catch (error) {
      toast.error(t('profileEditor.toast.imageDeleteError', `Error al eliminar imagen: ${error.message}`).replace('{message}', error.message));
    } finally {
      setUploadingImage('');
    }
  };

  const handleFileUpload = async (file, type) => {
    if (type === 'background-image' && !canUseBackgroundImage) return null;
    if (!profile?.id) { toast.error(t('profileEditor.toast.noProfile', 'No hay perfil seleccionado')); return null; }
    if (file.size > 5 * 1024 * 1024) { toast.error(t('profileEditor.toast.imageTooBig', 'La imagen es demasiado grande. Máximo 5MB')); return null; }
    if (!file.type.startsWith('image/')) { toast.error(t('profileEditor.toast.mustBeImage', 'El archivo debe ser una imagen')); return null; }
    setImageToCrop({ file, type });
    return null;
  };

  const handleCroppedImageUpload = async (croppedBlob, type) => {
    setUploadingImage(type);
    const fd = new FormData();
    fd.append('file', croppedBlob, 'cropped-image.jpg');
    try {
      const response = await axios.post(`${API}/upload/${type}/${profile.id}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      toast.success(t('profileEditor.toast.imageUpdated', 'Imagen actualizada correctamente'));
      if (refreshProfile) await refreshProfile(profile.id);
      return response.data;
    } catch (error) {
      const msg = error.response?.data?.detail || error.response?.data?.message || (error.request ? t('profileEditor.toast.serverConnectError', 'No se pudo conectar con el servidor') : error.message || t('profileEditor.toast.unknownError', 'Error desconocido'));
      toast.error(msg);
      return null;
    } finally {
      setUploadingImage('');
    }
  };

  // Hasta 3 archivos por perfil. El slot 1 usa los nombres de campo
  // originales (custom_file_url/custom_file_name, sin sufijo) para no
  // requerir migración de los perfiles que ya tenían un archivo subido;
  // los slots 2 y 3 siguen la misma convención _N que custom_link_N.
  const customFileFieldSuffix = (n) => (n === 1 ? '' : `_${n}`);

  const handleCustomFileUpload = async (e, slot = 1) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!profile?.id) { toast.error(t('profileEditor.toast.noProfile', 'No hay perfil seleccionado')); return; }
    if (file.size > 3 * 1024 * 1024) { toast.error(t('profileEditor.toast.fileTooBig', 'El archivo es demasiado grande. Máximo 3MB — intenta comprimirlo antes de subirlo')); return; }
    const nameField = `custom_file_name${customFileFieldSuffix(slot)}`;
    let fileName = formData[nameField]?.trim();
    if (!fileName) {
      const nameFromFile = file.name.includes('.') ? file.name.split('.').slice(0, -1).join('.') : file.name;
      fileName = nameFromFile || t('profileEditor.document', 'Documento');
      handleChange(nameField, fileName);
    }
    setUploadingFile(slot);
    try {
      const uploadFD = new FormData();
      uploadFD.append('file', file);
      await axios.post(
        `${API}/profiles/upload-custom-file/${profile.id}?custom_file_name=${encodeURIComponent(fileName)}&slot=${slot}`,
        uploadFD,
        { headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success(t('profileEditor.toast.fileUploaded', 'Archivo subido correctamente'));
      if (refreshProfile) await refreshProfile(profile.id);
    } catch (error) {
      const msg = error.response?.data?.detail || error.response?.data?.message || (error.request ? t('profileEditor.toast.serverConnectError', 'No se pudo conectar con el servidor') : error.message || t('profileEditor.toast.unknownError', 'Error desconocido'));
      toast.error(msg);
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDeleteCustomFile = async (slot = 1) => {
    const urlField = `custom_file_url${customFileFieldSuffix(slot)}`;
    if (!profile?.id || !profile?.[urlField]) return;
    if (!(await confirm(t('profileEditor.confirm.deleteFile', '¿Estás seguro de eliminar este archivo?'), { confirmLabel: t('profileEditor.delete', 'Eliminar') }))) return;
    try {
      await axios.delete(`${API}/profiles/custom-file/${profile.id}?slot=${slot}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      toast.success(t('profileEditor.toast.fileDeleted', 'Archivo eliminado correctamente'));
      handleChange(`custom_file_name${customFileFieldSuffix(slot)}`, '');
      if (refreshProfile) await refreshProfile(profile.id);
    } catch {
      toast.error(t('profileEditor.toast.fileDeleteError', 'No se pudo eliminar el archivo'));
    }
  };

  const handleRenameCustomFile = async (newName, slot = 1) => {
    if (!profile?.id || !newName.trim()) return;
    try {
      await axios.put(
        `${API}/profiles/custom-file-name/${profile.id}?name=${encodeURIComponent(newName.trim())}&slot=${slot}`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (refreshProfile) await refreshProfile(profile.id);
    } catch { /* silent */ }
  };

  // ── Image preview helper ─────────────────────────────────────────────────
  const imgSrc = (p, prefix) =>
    p[`${prefix}_url`]
      ? p[`${prefix}_url`]
      : p[`${prefix}_base64`]
      ? `data:${p[`${prefix}_type`] || 'image/jpeg'};base64,${p[`${prefix}_base64`]}`
      : p[prefix]
      ? `${API}/files/${p[prefix]}`
      : null;

  if (!profile) {
    return (
      <div className="pe-mobile-empty" style={{ textAlign: 'center', padding: '48px 0' }}>
        <style>{PE_MOBILE_CSS}</style>
        <p style={{ color: T.gray, fontSize: 14, fontFamily: T.font }}>{t('profileEditor.selectProfile', 'Selecciona un perfil para editar')}</p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <style>{CSS}</style>
      <style>{PE_MOBILE_CSS}</style>

      {/* Image Crop Modal */}
      {imageToCrop && (
        <ImageCropModal
          imageFile={imageToCrop.file}
          imageType={imageToCrop.type}
          onSave={(blob) => { handleCroppedImageUpload(blob, imageToCrop.type); setImageToCrop(null); }}
          onCancel={() => setImageToCrop(null)}
        />
      )}

      {/* ── Page header ─────────────────────────────────────────────────── */}
      <div className="pe-mobile-header" style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 28, fontWeight: 300, color: T.dark, letterSpacing: '-0.01em', margin: 0, fontFamily: PE_FONT }}>
          {t('profileEditor.title', 'Editar Perfil')}
        </h1>
        <p style={{ fontSize: 13, color: T.gray, marginTop: 4, letterSpacing: '-0.01em', fontFamily: T.font }}>
          {profile.name} {profile.lastname}
          {' · '}
          <span style={{ color: T.blue }}>/pages/{profile.slug}</span>
        </p>
      </div>

      {/* ══ IMAGES ═══════════════════════════════════════════════════════ */}
      <div className="pe-section">
        <h3 className="pe-sec-title">{t('profileEditor.images.title', 'Imágenes')}</h3>
        <div className="grid grid-cols-2 gap-5 pe-mobile-grid">

          {/* Profile image */}
          {(() => {
            const src = imgSrc(profile, 'profile_image');
            return (
              <div>
                <label className="pe-label">{t('profileEditor.images.profilePhoto', 'Foto de Perfil')}</label>
                {src ? (
                  <img className="pe-avatar-preview" src={src} alt={t('profileEditor.images.profileAlt', 'Perfil')} onError={e => e.target.style.display='none'}
                    style={{ width: '100%', height: 148, objectFit: 'cover', borderRadius: 12, marginBottom: 8, border: `1px solid ${T.border}` }}
                  />
                ) : (
                  <div className="pe-img-drop">
                    <svg width="32" height="32" fill="none" stroke={T.light} strokeWidth="1.5" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                    </svg>
                    <span style={{ fontSize: 12, color: T.light, fontFamily: T.font }}>{t('profileEditor.images.noImage', 'Sin imagen')}</span>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <label style={{ flex: 1, cursor: 'pointer' }}>
                    <input type="file" accept="image/*" className="hidden" disabled={uploadingImage === 'profile-image'}
                      onChange={e => e.target.files[0] && handleFileUpload(e.target.files[0], 'profile-image')} />
                    <div className="pe-upload-btn" style={{ pointerEvents: uploadingImage === 'profile-image' ? 'none' : 'auto' }}>
                      <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                      </svg>
                      {uploadingImage === 'profile-image' ? t('profileEditor.uploading', 'Subiendo…') : t('profileEditor.images.uploadPhoto', 'Subir foto')}
                    </div>
                  </label>
                  {src && (
                    <button className="pe-del-btn" disabled={uploadingImage === 'profile-image'}
                      onClick={() => handleImageDelete('profile-image')} style={{ flex: '0 0 auto', width: 'auto', padding: '9px 14px' }}>
                      <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Background image */}
          {(() => {
            const src = imgSrc(profile, 'background_image');
            return (
              <div>
                <label className="pe-label">{t('profileEditor.images.backgroundImage', 'Imagen de Fondo')}</label>
                {src ? (
                  <img className="pe-avatar-preview" src={src} alt={t('profileEditor.images.backgroundAlt', 'Fondo')} onError={e => e.target.style.display='none'}
                    style={{ width: '100%', height: 148, objectFit: 'cover', borderRadius: 12, marginBottom: 8, border: `1px solid ${T.border}` }}
                  />
                ) : (
                  <div className="pe-img-drop">
                    <svg width="32" height="32" fill="none" stroke={T.light} strokeWidth="1.5" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                    </svg>
                    <span style={{ fontSize: 12, color: T.light, fontFamily: T.font }}>{t('profileEditor.images.noBackground', 'Sin imagen de fondo')}</span>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <label
                    style={{ flex: 1, cursor: canUseBackgroundImage ? 'pointer' : 'not-allowed' }}
                    data-locked-tip={!canUseBackgroundImage ? t('profileEditor.requiresPaidPlan', 'Requiere un plan de pago') : undefined}
                  >
                    <input type="file" accept="image/*" className="hidden" disabled={uploadingImage === 'background-image' || !canUseBackgroundImage}
                      onChange={e => e.target.files[0] && handleFileUpload(e.target.files[0], 'background-image')} />
                    <div
                      className={`pe-upload-btn ${!canUseBackgroundImage ? 'pe-locked' : ''}`}
                      style={{ pointerEvents: uploadingImage === 'background-image' ? 'none' : 'auto' }}
                    >
                      <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                      </svg>
                      {uploadingImage === 'background-image' ? t('profileEditor.uploading', 'Subiendo…') : t('profileEditor.images.uploadBackground', 'Subir fondo')}
                    </div>
                  </label>
                  {src && (
                    <button className="pe-del-btn" disabled={uploadingImage === 'background-image'}
                      onClick={() => handleImageDelete('background-image')} style={{ flex: '0 0 auto', width: 'auto', padding: '9px 14px' }}>
                      <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* ══ PERSONAL INFO ════════════════════════════════════════════════ */}
      <div className="pe-section">
        <h3 className="pe-sec-title">{t('profileEditor.personal.title', 'Información Personal')}</h3>
        <div className="grid grid-cols-2 gap-4 pe-mobile-grid">
          <Field label={t('profileEditor.personal.name', 'Nombre')}>
            <input className="pe-input" type="text" value={formData.name}
              onChange={e => handleChange('name', e.target.value)} />
          </Field>
          <Field label={t('profileEditor.personal.lastname', 'Apellido')}>
            <input className="pe-input" type="text" value={formData.lastname}
              onChange={e => handleChange('lastname', e.target.value)} />
          </Field>
          <Field label={t('profileEditor.personal.email', 'Correo Electrónico')}>
            <input className="pe-input" type="email" value={formData.email}
              placeholder={t('profileEditor.personal.emailPlaceholder', 'correo@ejemplo.com')}
              onChange={e => handleChange('email', e.target.value)} />
          </Field>
          <Field label={t('profileEditor.personal.position', 'Cargo / Posición')}>
            <input className="pe-input" type="text" value={formData.position}
              onChange={e => handleChange('position', e.target.value)} />
          </Field>
          <div className="col-span-2">
            <Field label={t('profileEditor.personal.company', 'Empresa')}>
              <input className="pe-input" type="text" value={formData.company}
                onChange={e => handleChange('company', e.target.value)} />
            </Field>
          </div>
          <div className="col-span-2">
            <Field label={t('profileEditor.personal.bio', 'Biografía')}>
              <textarea className="pe-input" rows={4} value={formData.bio}
                placeholder={t('profileEditor.personal.bioPlaceholder', 'Cuéntanos sobre ti y tu trabajo…')}
                onChange={e => handleChange('bio', e.target.value)}
                style={{ resize: 'vertical', fontFamily: T.font }} />
            </Field>
          </div>
        </div>
      </div>

      {/* ══ CONTACT LINKS ════════════════════════════════════════════════ */}
      <div className="pe-section">
        <h3 className="pe-sec-title">{t('profileEditor.contact.title', 'Redes y Contacto')}</h3>
        <div className="grid grid-cols-2 gap-4 pe-mobile-grid">
          <Field label={t('profileEditor.contact.phone', 'Teléfono (archivo VCF)')} hint={t('profileEditor.contact.phoneHint', 'Aparece en el contacto para llamadas directas')}>
            <input className="pe-input" type="tel" value={formData.phone}
              placeholder="+56 9 1234 5678"
              onChange={e => handleChange('phone', e.target.value)} />
          </Field>
          <Field label="WhatsApp">
            <input className="pe-input" type="text" value={formData.whatsapp}
              placeholder="+56 9 1234 5678"
              onChange={e => handleChange('whatsapp', e.target.value)} />
          </Field>
          <Field label="Instagram">
            <input className="pe-input" type="text" value={formData.instagram}
              placeholder={t('profileEditor.contact.usernamePlaceholder', '@usuario')}
              onChange={e => handleChange('instagram', e.target.value)} />
          </Field>
          <Field label="TikTok">
            <input className="pe-input" type="text" value={formData.tiktok}
              placeholder={t('profileEditor.contact.usernamePlaceholder', '@usuario')}
              onChange={e => handleChange('tiktok', e.target.value)} />
          </Field>
          <Field label="LinkedIn">
            <input className="pe-input" type="text" value={formData.linkedin}
              placeholder={t('profileEditor.contact.linkedinPlaceholder', 'https://linkedin.com/in/usuario')}
              onChange={e => handleChange('linkedin', e.target.value)} />
          </Field>
          <Field label="Facebook">
            <input className="pe-input" type="text" value={formData.facebook}
              placeholder={t('profileEditor.contact.facebookPlaceholder', 'https://facebook.com/usuario')}
              onChange={e => handleChange('facebook', e.target.value)} />
          </Field>
          <Field label={t('profileEditor.contact.website', 'Sitio Web')}>
            <input className="pe-input" type="text" value={formData.website}
              placeholder={t('profileEditor.contact.websitePlaceholder', 'https://mi-sitio.com')}
              onChange={e => handleChange('website', e.target.value)} />
          </Field>
        </div>
      </div>

      {/* ══ CUSTOM LINKS ═════════════════════════════════════════════════ */}
      <div className="pe-section">
        <h3 className="pe-sec-title">{t('profileEditor.links.title', 'Enlaces Personalizados')}</h3>
        <p style={{ fontSize: 13, color: T.gray, marginBottom: 16, marginTop: -8, fontFamily: T.font }}>
          {t('profileEditor.links.sub', 'Hasta 4 enlaces que se mostrarán en tu perfil público.')}
        </p>
        {[1, 2, 3, 4].map(n => (
          <div key={n} className="pe-link-row">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <span className="pe-link-num">{n}</span>
              <span style={{ fontSize: 12, fontWeight: 500, color: T.gray, fontFamily: T.font, letterSpacing: '-0.01em' }}>
                {t('profileEditor.links.link', 'Enlace')} {n}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3 pe-mobile-grid">
              <Field label="URL">
                <input className="pe-input" type="text" placeholder="https://…"
                  value={formData[`custom_link_${n}`]}
                  onChange={e => handleChange(`custom_link_${n}`, e.target.value)} />
              </Field>
              <Field label={t('profileEditor.links.linkTitle', 'Título')}>
                <input className="pe-input" type="text" placeholder={[t('profileEditor.links.placeholder1', 'Mi Portafolio'), t('profileEditor.links.placeholder2', 'Mi Catálogo'), t('profileEditor.links.placeholder3', 'Servicios'), t('profileEditor.links.placeholder4', 'Contacto')][n-1]}
                  value={formData[`custom_link_title_${n}`]}
                  onChange={e => handleChange(`custom_link_title_${n}`, e.target.value)} />
              </Field>
            </div>
          </div>
        ))}
      </div>

      {/* ══ CUSTOM FILES (hasta 2) ═══════════════════════════════════════ */}
      <div className="pe-section">
        <h3 className="pe-sec-title">{t('profileEditor.files.title', 'Archivos Personalizados')}</h3>
        <p style={{ fontSize: 13, color: T.gray, marginBottom: 16, marginTop: -8, fontFamily: T.font }}>
          {t('profileEditor.files.sub', 'Hasta 2 PDF u otros documentos que aparecerán como botones en tu perfil.')}
        </p>
        {[1, 2].map(n => {
          const suffix = customFileFieldSuffix(n);
          const urlField = `custom_file_url${suffix}`;
          const nameField = `custom_file_name${suffix}`;
          const fileUrl = profile?.[urlField];
          return (
            <div key={n} className="pe-link-row">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <span className="pe-link-num">{n}</span>
                <span style={{ fontSize: 12, fontWeight: 500, color: T.gray, fontFamily: T.font, letterSpacing: '-0.01em' }}>
                  {t('profileEditor.files.file', 'Archivo')} {n}
                </span>
              </div>

              {fileUrl && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12,
                  padding: '10px 14px', borderRadius: 10,
                  background: 'rgba(52,199,89,0.08)', border: '1px solid rgba(52,199,89,0.2)',
                }}>
                  <svg width="16" height="16" fill="none" stroke={T.green} strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  <input
                    data-testid={`custom-file-name-input-${n}`}
                    type="text" value={formData[nameField]}
                    onChange={e => handleChange(nameField, e.target.value)}
                    onBlur={e => handleRenameCustomFile(e.target.value, n)}
                    placeholder={t('profileEditor.files.fileName', 'Nombre del archivo')}
                    style={{
                      flex: 1, background: 'transparent', border: 'none', outline: 'none',
                      fontSize: 13, fontWeight: 500, color: '#1a7f3c', fontFamily: T.font,
                      letterSpacing: '-0.01em',
                    }}
                  />
                  <a href={fileUrl} target="_blank" rel="noopener noreferrer"
                    style={{ color: T.blue, display: 'flex', flexShrink: 0 }}>
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                    </svg>
                  </a>
                  <button data-testid={`custom-file-delete-btn-${n}`} onClick={() => handleDeleteCustomFile(n)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.red, display: 'flex', flexShrink: 0 }}>
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                    </svg>
                  </button>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 pe-mobile-grid">
                <Field label={t('profileEditor.files.buttonName', 'Nombre del botón (opcional)')} hint={t('profileEditor.files.buttonNameHint', 'Si no escribes nombre, se usa el del archivo')}>
                  <input className="pe-input" type="text" value={formData[nameField]}
                    placeholder={t('profileEditor.files.buttonNamePlaceholder', 'Se usará el nombre del archivo')}
                    onChange={e => handleChange(nameField, e.target.value)} />
                </Field>
                <Field label={t('profileEditor.files.uploadFile', 'Subir archivo')} hint={t('profileEditor.files.uploadHint', 'Máximo 3MB · PDF, DOC, XLS, PPT, ZIP · Si pesa más, comprímelo antes de subirlo')}>
                  <label style={{ cursor: uploadingFile === n ? 'not-allowed' : 'pointer' }}>
                    <input type="file" className="hidden" disabled={uploadingFile === n}
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip"
                      onChange={e => handleCustomFileUpload(e, n)} />
                    <div className="pe-upload-btn" style={{ opacity: uploadingFile === n ? 0.4 : 1 }}>
                      <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/>
                      </svg>
                      {uploadingFile === n ? t('profileEditor.uploading', 'Subiendo…') : fileUrl ? t('profileEditor.files.replaceFile', 'Reemplazar archivo') : t('profileEditor.files.uploadFile', 'Subir archivo')}
                    </div>
                  </label>
                </Field>
              </div>
            </div>
          );
        })}
      </div>

      {/* ══ TRANSFER DETAILS ═════════════════════════════════════════════ */}
      <div className="pe-section">
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: formData.transfer_enabled ? 16 : 0 }}>
          <div>
            <h3 className="pe-sec-title" style={{ marginBottom: 4 }}>{t('profileEditor.transfer.title', 'Datos de Transferencia')}</h3>
            <p style={{ fontSize: 13, color: T.gray, fontFamily: T.font, letterSpacing: '-0.01em', margin: 0 }}>
              {t('profileEditor.transfer.sub', 'Se muestran en tu perfil público con un botón para copiar cada dato.')}
            </p>
          </div>
          <button
            type="button"
            className="pe-toggle"
            data-on={formData.transfer_enabled}
            data-testid="transfer-enabled-toggle"
            aria-label={t('profileEditor.transfer.toggleAria', 'Mostrar datos de transferencia en mi perfil público')}
            onClick={() => handleChange('transfer_enabled', !formData.transfer_enabled)}
          >
            <span className="pe-toggle-knob" />
          </button>
        </div>

        {formData.transfer_enabled && (
          <div className="grid grid-cols-2 gap-4 pe-mobile-grid">
            <Field label={t('profileEditor.transfer.bank', 'Banco')}>
              <input className="pe-input" type="text" value={formData.transfer_bank}
                placeholder={t('profileEditor.transfer.bankPlaceholder', 'Banco Estado, Banco de Chile…')}
                onChange={e => handleChange('transfer_bank', e.target.value)} />
            </Field>
            <Field label={t('profileEditor.transfer.accountType', 'Tipo de cuenta')}>
              <select className="pe-input" value={formData.transfer_account_type}
                onChange={e => handleChange('transfer_account_type', e.target.value)}>
                <option value="">{t('profileEditor.transfer.accountTypePlaceholder', 'Selecciona…')}</option>
                <option value="corriente">{t('profileEditor.transfer.accountTypeCorriente', 'Cuenta Corriente')}</option>
                <option value="vista">{t('profileEditor.transfer.accountTypeVista', 'Cuenta Vista')}</option>
                <option value="ahorro">{t('profileEditor.transfer.accountTypeAhorro', 'Cuenta de Ahorro')}</option>
                <option value="rut">{t('profileEditor.transfer.accountTypeRut', 'Cuenta RUT')}</option>
              </select>
            </Field>
            <Field label={t('profileEditor.transfer.accountNumber', 'Número de cuenta')}>
              <input className="pe-input" type="text" value={formData.transfer_account_number}
                onChange={e => handleChange('transfer_account_number', e.target.value)} />
            </Field>
            <Field label={t('profileEditor.transfer.holderName', 'Nombre del titular')}>
              <input className="pe-input" type="text" value={formData.transfer_holder_name}
                placeholder={`${formData.name} ${formData.lastname}`.trim()}
                onChange={e => handleChange('transfer_holder_name', e.target.value)} />
            </Field>
            <Field label="RUT">
              <input className="pe-input" type="text" value={formData.transfer_rut}
                placeholder="12.345.678-9"
                onChange={e => handleChange('transfer_rut', e.target.value)} />
            </Field>
            <Field label={t('profileEditor.transfer.email', 'Email para comprobante')} hint={t('profileEditor.transfer.emailHint', 'Opcional')}>
              <input className="pe-input" type="email" value={formData.transfer_email}
                onChange={e => handleChange('transfer_email', e.target.value)} />
            </Field>
          </div>
        )}
      </div>

      {/* ══ SAVE ═════════════════════════════════════════════════════════ */}
      <div className="pe-mobile-save" style={{ display: 'flex', justifyContent: 'flex-end', paddingBottom: 24 }}>
        <button className="pe-save-btn" onClick={handleSave} disabled={saving} data-tutorial="save-changes">
          {saving ? (
            <>
              <div style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'im-rotate .8s linear infinite' }} />
              {t('profileEditor.saving', 'Guardando…')}
            </>
          ) : t('profileEditor.saveChanges', 'Guardar cambios')}
        </button>
      </div>
    </div>
  );
};

export default ProfileEditor;
